import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import DuplicateIcon from "@locaisolutions/icons/dist/icons20px/Duplicate20Px";
import AuditLogsIcon from "@locaisolutions/icons/dist/icons20px/FileAslog20Px";

import SiteConfigurations from "./SiteConfigurations";
import ConfigurationItems from "./ConfigurationItems";
import {
  getCurrentlyUsedEnvironmentsForSelectedSite,
  getCurrentSiteConfigurationTypes,
  getHasRequiredSiteOperationData,
  getSelectedSiteSection,
  getSiteOperationTempData,
  selectArePendingChangesAvailable
} from "../../redux/selectors/siteSelectors";
import Header from "./Header";
import PendingChangesList from "./PendingChangesList";
import {
  clearSiteOperationTempData,
  setInitialSiteOperationDetails,
  updateOpenSiteView
} from "./siteSlice";
import { useAppDispatch } from "../../store";
import DeploymentEventsStatusDrawer from "../deploymentsStatus/DeploymentEventsStatusDrawer";
import { useGetDeploymentsStatusQuery } from "../../redux/api/site";
import { getSelectedDrawersData } from "../../redux/selectors/drawerSelectors";
import { emptyGuid } from "../../lib/siteHelpers";
import CloudPlusIcon from "../../assets/ClousPlus";
import AddNewConfigurationModal from "../addNewConfiguration/AddNewConfigurationModal";
import AddNewEnvironmentModal from "../addNewSiteEnvironment/AddNewEnvironmentModal";
import DuplicateSiteModal from "../duplicate/DuplicateSiteModal";

export const MainContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  height: `calc(100% - 110px)`,
  gridTemplateRows: "60px 1fr",
  padding: 10,
  borderRadius: "6px",
  background: theme.palette.secondary.dark,
  margin: 15
}));

const SecondaryContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  border: `1px 0px 0px 0px`,
  borderColor: theme.palette.divider,
  borderRadius: "6px",
  background: theme.palette.background.paper,
  height: "100%",
  overflow: "hidden"
}));

function MainSiteContainer() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isDuplicateSiteOpen, setIsDuplicateSiteOpen] =
    useState<boolean>(false);
  const [isAddNewEnvironmentModalOpen, updateAddNewEnvironmentModalStatus] =
    useState<boolean>(false);
  const [isAddNewConfigurationModal, updateAddNewConfigurationModalStatus] =
    useState<boolean>(false);

  const selectedSiteDrawersData = useSelector(getSelectedDrawersData).site;
  const selectedSiteSection = useSelector(getSelectedSiteSection);
  const isSiteSectionSelected = !!selectedSiteSection.id;
  const hasPendingChanges = useSelector(selectArePendingChangesAvailable);
  const currentlyUsedEnvironments = useSelector(
    getCurrentlyUsedEnvironmentsForSelectedSite
  );
  const hasSiteConfigurationTypes =
    useSelector(getCurrentSiteConfigurationTypes).numberOfConfigurationsTypes >
    0;

  const [isDeploymentEventsDrawerOpen, updateDeploymentsDrawerStatus] =
    useState(false);

  const siteOperationTempData = useSelector(getSiteOperationTempData);
  const hasRequiredSiteOperationData = useSelector(
    getHasRequiredSiteOperationData
  );
  const isInNewSiteCreatingMode =
    siteOperationTempData.mode === "newSiteCreation" &&
    hasRequiredSiteOperationData;

  const selectedClientDrawerData = useSelector(getSelectedDrawersData).site;
  useGetDeploymentsStatusQuery(
    {
      siteId: selectedClientDrawerData?.id || ""
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !selectedClientDrawerData?.id || hasRequiredSiteOperationData
    }
  );
  const siteViewActions = useMemo(
    (): {
      icon: JSX.Element;
      name: string;
      onClickBtn: () => void;
      disabled: boolean;
    }[] => [
      {
        icon: (
          <DuplicateIcon
            height={30}
            width={30}
            aria-label="duplicate-site-button-icon"
          />
        ),
        name: "Duplicate Site",
        onClickBtn: () => {
          setIsDuplicateSiteOpen(true);
        },
        disabled: !currentlyUsedEnvironments.length || false
      },
      {
        icon: (
          <CloudPlusIcon
            sx={{
              height: "28px",
              width: "28px"
            }}
            aria-label="add-environment-button-icon"
          />
        ),
        name: "Add Environment",
        onClickBtn: () => {
          updateAddNewEnvironmentModalStatus(true);
        },
        disabled: !currentlyUsedEnvironments.length || false
      },
      {
        icon: (
          <AuditLogsIcon
            height={20}
            width={20}
            aria-label="audit logs button"
          />
        ),
        name: "Audit Logs",
        onClickBtn: () => {
          dispatch(updateOpenSiteView("AuditLog"));
        },
        disabled: false
      }
    ],
    [
      currentlyUsedEnvironments,
      hasSiteConfigurationTypes,
      dispatch,
      updateOpenSiteView
    ]
  );

  useEffect(() => {
    const siteIdFromPath = location.pathname.split("/")?.[2];
    if (
      !selectedSiteDrawersData?.id ||
      (!siteIdFromPath && !selectedSiteDrawersData?.id)
    )
      return;

    if (selectedSiteDrawersData.id === emptyGuid && isInNewSiteCreatingMode) {
      navigate("/site/new", {
        replace: true
      });
    } else if (selectedSiteDrawersData.id !== siteIdFromPath) {
      navigate(`/site/${selectedSiteDrawersData.id}`, {
        replace: true
      });
    }
  }, [selectedSiteDrawersData, isInNewSiteCreatingMode]);

  useEffect(() => {
    return () => {
      dispatch(clearSiteOperationTempData({ shouldHardReset: false }));
    };
  }, []);
  const deploymentStatusDrawerWidth = !isDeploymentEventsDrawerOpen
    ? "51px"
    : "300px";

  const requestNewConfiguration = (requestNewSiteConfigurationType: {
    id: string;
    name: string;
    onePerEnvironment: boolean;
    subConfigurations: SiteConfigurationMenuItem[];
    parentConfigurationId: string | null;
    additionalSubConfigurationDetails?: SiteConfigurationMenuItem;
  }) => {
    dispatch(
      setInitialSiteOperationDetails({
        mode: "newSiteConfiguration",
        requestNewSiteConfigurationType
      })
    );
    updateAddNewConfigurationModalStatus(true);
  };

  return (
    <MainContainer
      sx={{
        ...(hasRequiredSiteOperationData && {
          border: ({ palette }) => `3px solid ${palette.primary.main}`
        })
      }}
      aria-label="site details container"
    >
      <Header
        shouldRenderPendingChanges={hasPendingChanges}
        siteViewActions={siteViewActions}
      />
      <SecondaryContainer
        sx={{
          gridTemplateColumns: isInNewSiteCreatingMode
            ? "minmax(200px, 250px) minmax(450px, 1fr)"
            : hasPendingChanges
              ? `minmax(200px, 250px) minmax(450px, 1fr) minmax(270px, 300px) minmax(${isDeploymentEventsDrawerOpen ? "270px" : "51px"} , ${deploymentStatusDrawerWidth})`
              : `minmax(200px, 250px) minmax(450px, 1fr) ${deploymentStatusDrawerWidth}`
        }}
      >
        <SiteConfigurations
          siteOperationTempData={siteOperationTempData}
          requestNewConfiguration={requestNewConfiguration}
        />
        {isSiteSectionSelected && (
          <ConfigurationItems siteOperationTempData={siteOperationTempData} />
        )}
        {hasPendingChanges && (
          <PendingChangesList siteOperationTempData={siteOperationTempData} />
        )}
        {!isInNewSiteCreatingMode && (
          <DeploymentEventsStatusDrawer
            isDeploymentEventsDrawerOpen={isDeploymentEventsDrawerOpen}
            updateDeploymentsDrawerStatus={(isDrawerOpen) => {
              updateDeploymentsDrawerStatus(isDrawerOpen);
            }}
          />
        )}
      </SecondaryContainer>
      {isDuplicateSiteOpen && (
        <DuplicateSiteModal
          isModalOpen={isDuplicateSiteOpen}
          handleModalClose={() => {
            setIsDuplicateSiteOpen(!isDuplicateSiteOpen);
          }}
        />
      )}
      {isAddNewEnvironmentModalOpen && (
        <AddNewEnvironmentModal
          isModalOpen={isAddNewEnvironmentModalOpen}
          onClose={() => {
            updateAddNewEnvironmentModalStatus(false);
          }}
        />
      )}
      {isAddNewConfigurationModal && (
        <AddNewConfigurationModal
          isOpen={isAddNewConfigurationModal}
          onClose={() => {
            updateAddNewConfigurationModalStatus(false);
          }}
        />
      )}
    </MainContainer>
  );
}

export default MainSiteContainer;
